// Copyright ©️ 2026 eVolve MEP, LLC

import { useState } from 'react';

import { type ProductPool, formatDate } from '@evolvemep/api-ts';
import { Anchor, Group, RingProgress, Text } from '@mantine/core';
import { differenceInDays } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';

import { LicenseIcon } from 'assets/icons/LicenseIcon';
import { InfoIcon } from 'components/Mantine/InfoIcon';
import { EvolveProductCard } from 'modules/Admin/Licenses/LicensesMantine/EvolveProductCard';

const WARNING_COLOR_ON = 60;
const DANGER_COLOR_ON = 30;

/**
 * We don't actually store when the user last renewed, just the start of their contract, so we have some long contracts.
 * To get around that, we display the time left as a percentage of a year.
 * Do not allow it to exceed 100%.
 */
const getContractRemainingPercent = (remainingDays: number) => Math.min(remainingDays / 365, 1);

export const ActiveProductCard = ({ productPool }: { productPool: ProductPool }) => {
  const [now] = useState(() => toZonedTime(new Date(), 'UTC'));
  const remainingDays = differenceInDays(productPool.renewalDate, now);
  const renewalColor =
    remainingDays <= WARNING_COLOR_ON ? (remainingDays <= DANGER_COLOR_ON ? 'red.6' : 'orange') : undefined;
  const isExpired = remainingDays <= 0;
  const assignedQty = productPool.poolQuantity - productPool.poolAvailableQuantity;

  return (
    <EvolveProductCard
      title={productPool.product.productNameFormatted}
      icon={<LicenseIcon productId={productPool.productId} />}
      belowTitle={
        <Group gap="sm" c="gray.6">
          <Text c={renewalColor} fw={500}>
            {isExpired ? 'Expired' : 'Renewal'}: {formatDate(productPool.renewalDate, 'MM/dd/yyyy')}
          </Text>
          {productPool.product.usesEntitlements && (
            <>
              <Text fw={600}>&bull;</Text>
              <Text fw={500}>
                {`${assignedQty.toLocaleString()} of ${productPool.poolQuantity.toLocaleString()} assigned`}
              </Text>
            </>
          )}
        </Group>
      }
      endSection={
        isExpired ? (
          <Anchor
            rel="noreferrer"
            href="https://evolvemep.com/renew-licenses"
            target="_blank"
            style={{ width: '64px', height: '64px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <InfoIcon label="This product is expired. Click here to renew license." color="red.6" size="xl" />
          </Anchor>
        ) : (
          <RingProgress
            size={64}
            thickness={5}
            label={
              <Text size="xs" c="dimmed" style={{ textAlign: 'center', lineHeight: '1.2' }}>
                {remainingDays.toLocaleString()}
                <br />
                day{remainingDays !== 1 ? 's' : ''}
              </Text>
            }
            sections={[{ value: getContractRemainingPercent(remainingDays) * 100, color: renewalColor ?? 'teal' }]}
          />
        )
      }
    />
  );
};
